import React from 'react';
import { Box, Container, Typography, Grid, styled, Stack } from '@mui/material';
import { motion } from 'framer-motion';

// --- STYLED COMPONENTS ---

const HeroSection = styled(Box)({
    // Modern Mesh Gradient Background
    background: 'radial-gradient(circle at 80% 20%, #111827 0%, #000 100%)',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
    color: '#fff',
    top: 10,
});

// Animated Background Glows
const Blob = styled(motion.div)(({ color, size, top, left, right, bottom }) => ({
    position: 'absolute',
    width: size,
    height: size,
    background: `radial-gradient(circle, ${color} 0%, rgba(0,0,0,0) 70%)`,
    borderRadius: '50%',
    filter: 'blur(90px)',
    zIndex: 0,
    top, left, right, bottom,
    opacity: 0.3,
}));

const LargeTitle = styled(motion.h1)(({ theme }) => ({
    fontWeight: 900,
    lineHeight: 1,
    fontSize: 'clamp(3rem, 7vw, 5.5rem)',
    letterSpacing: '-2px',
    margin: 0,
    paddingBottom: theme.spacing(3),
}));

const ImageFrame = styled(motion.div)({
    position: 'relative',
    width: '100%',
    maxWidth: '420px',
    margin: '0 auto',
    zIndex: 2,
});

const StyledImage = styled(motion.img)({
    width: '100%',
    height: '540px',
    objectFit: 'cover',
    borderRadius: '30px',
    position: 'relative',
    zIndex: 1,
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 50px 100px -20px rgba(0,0,0,0.7)',
});

// --- ANIMATION VARIANTS ---

const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.3 } }
};

const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};

// --- COMPONENT ---

const AboutHero = () => {
    return (
        <HeroSection>
            {/* Background Modern Blobs */}
            <Blob color="#3b82f6" size="600px" top="-10%" right="-5%" 
                animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.3, 0.2] }}
                transition={{ duration: 12, repeat: Infinity }}
            />
            <Blob color="#6366f1" size="400px" bottom="5%" left="5%" 
                animate={{ x: [0, 40, 0], y: [0, -30, 0] }}
                transition={{ duration: 10, repeat: Infinity }}
            />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                <Grid container spacing={8} alignItems="center">
                    
                    {/* LEFT SIDE: Content (Opposite to previous) */}
                    <Grid item xs={12} md={7}>
                        <motion.div variants={containerVariants} initial="hidden" animate="visible">
                            
                            <motion.div variants={itemVariants}>
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                    <Box sx={{ width: 40, height: 2, bgcolor: '#3b82f6' }} />
                                    <Typography variant="overline" sx={{ color: '#3b82f6', fontWeight: 900, letterSpacing: 4 }}>
                                        BIO & JOURNEY
                                    </Typography>
                                </Stack>
                            </motion.div>

                            <LargeTitle variants={itemVariants}>
                                Crafting <br />
                                <span style={{ 
                                    background: 'linear-gradient(to right, #fff 20%, #3b82f6 100%)', 
                                    WebkitBackgroundClip: 'text', 
                                    WebkitTextFillColor: 'transparent' 
                                }}>
                                    High-End Apps
                                </span> <br />
                                with Purpose.
                            </LargeTitle>

                            <motion.div variants={itemVariants}>
                                <Typography sx={{
                                    color: 'rgba(255, 255, 255, 0.65)',
                                    fontSize: '1.2rem',
                                    lineHeight: 1.8,
                                    maxWidth: '600px',
                                    fontWeight: 400,
                                    mb: 5
                                }}>
                                    I am <b style={{ color: '#fff' }}>Vikash Tiwari</b>. A Software Developer who blends 
                                    technical logic with aesthetic design. Specialized in <span style={{ color: '#3b82f6' }}>Android & Flutter</span>, 
                                    I build applications that don't just work—they provide a premium user experience.
                                </Typography>
                            </motion.div>

                            {/* Stats */}
                            <Stack direction="row" spacing={6} component={motion.div} variants={itemVariants}>
                                <Box>
                                    <Typography variant="h3" sx={{ fontWeight: 900, mb: 0.5 }}>02+</Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 2 }}>YEARS OF CRAFT</Typography>
                                </Box>
                                <Box sx={{ width: '1px', height: '50px', bgcolor: 'rgba(255,255,255,0.1)' }} />
                                <Box>
                                    <Typography variant="h3" sx={{ fontWeight: 900, mb: 0.5 }}>10+</Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 2 }}>PRO PROJECTS</Typography>
                                </Box>
                            </Stack>
                        </motion.div>
                    </Grid>

                    {/* RIGHT SIDE: Image (Wapis Right Par) */}
                    <Grid item xs={12} md={5}>
                        <ImageFrame
                            initial={{ opacity: 0, x: 100 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <StyledImage
                                whileHover={{ scale: 1.03, rotate: 1 }}
                                src="/vikashTiwariImg.jpg"
                                alt="Vikash Tiwari Software Developer"
                            />
                            
                            {/* Position Badge - Set to Bottom Right */}
                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 1, type: 'spring' }}
                                style={{
                                    position: 'absolute', 
                                    bottom: '-25px', 
                                    right: '-20px', // Right side bottom placement
                                    background: 'rgba(59, 130, 246, 0.1)',
                                    backdropFilter: 'blur(25px)',
                                    padding: '20px 30px',
                                    borderRadius: '20px',
                                    border: '1px solid rgba(59, 130, 246, 0.3)',
                                    zIndex: 3,
                                    boxShadow: '0 25px 50px rgba(0,0,0,0.5)'
                                }}
                            >
                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 900, color: '#3b82f6', letterSpacing: 2, mb: 0.5 }}>CURRENT ROLE</Typography>
                                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#fff' }}>Software Developer</Typography>
                            </motion.div>
                        </ImageFrame>
                    </Grid>

                </Grid>
            </Container>
        </HeroSection>
    );
};

export default AboutHero;
